import { useState } from "react";
import {
	Plus,
	FileText,
	FolderOpen,
	CheckCircle2,
	FilePlus
} from "lucide-react";
import { Button } from "src/components/ui/button";
import { Link, useSearchParams } from "react-router-dom";
import { useObservationCategories } from "src/features/Admin/ObservationsCategories/hooks/useObservationCategories";
import { FormCategoriesList } from "src/features/observations/containers/FormCategoriesList";
import { FormStatusTabPanel } from "src/features/observations/containers/FormStatusTabPanel";
import { CreateNewCategoryModal } from "src/features/Admin/Observations/modals/CreateNewCategoryModal";
import { UserAdminFilter } from "src/containers/UserAdminFilter";

const EditForms = () => {
	const [params] = useSearchParams();
	const category = params.get("category") || "";
	const categoryId = parseInt(params.get("categoryId") || "");

	const { data: observationCategories, isLoading: isCategoriesLoading } =
		useObservationCategories();

	const [isOpen, setIsOpen] = useState(false);

	return (
		<div aria-label="All published/draft forms">
			{!category && (
				<div>
					{!isCategoriesLoading && observationCategories && (
						<div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
							{[
								{
									label: "Total Forms",
									value: observationCategories.reduce(
										(sum, c) => sum + c.publishedFormsCount + c.draftFormsCount,
										0
									),
									icon: FileText,
									color: "text-primary"
								},
								{
									label: "Published",
									value: observationCategories.reduce(
										(sum, c) => sum + c.publishedFormsCount,
										0
									),
									icon: CheckCircle2,
									color: "text-emerald-500"
								},
								{
									label: "Drafts",
									value: observationCategories.reduce(
										(sum, c) => sum + c.draftFormsCount,
										0
									),
									icon: FilePlus,
									color: "text-zinc-500"
								},
								{
									label: "Categories",
									value: observationCategories.length,
									icon: FolderOpen,
									color: "text-amber-500"
								}
							].map((stat) => (
								<div
									key={stat.label}
									className="rounded-xl border border-border bg-white p-5 shadow-sm"
								>
									<div className="flex items-center gap-3 mb-2">
										<stat.icon className={`h-5 w-5 ${stat.color}`} />
										<span className="text-sm text-muted-foreground">
											{stat.label}
										</span>
									</div>
									<p className="font-heading text-2xl font-semibold text-foreground">
										{stat.value}
									</p>
								</div>
							))}
						</div>
					)}

					<div className="flex items-center justify-between mb-6">
						<h2 className="font-heading text-lg font-medium text-foreground">
							Categories
						</h2>

						<UserAdminFilter requiredPermission="CanViewEditObservationForms">
							<div className="flex items-center gap-3">
								<Button
									type="button"
									variant="outline"
									onClick={() => setIsOpen(true)}
								>
									<Plus className="h-4 w-4" />
									New Category
								</Button>

								<Link
									to={
										categoryId
											? `/observations/forms/new-observation-form?categoryId=${categoryId}`
											: "/observations/forms/new-observation-form"
									}
									className="inline-flex items-center gap-2 px-4 h-10 rounded-lg bg-primary text-white text-sm font-medium shadow-sm hover:bg-primary-dark transition-colors"
								>
									<Plus className="h-4 w-4" />
									New Form
								</Link>
							</div>
						</UserAdminFilter>
					</div>

					{isCategoriesLoading ? (
						<div className="flex justify-center py-12">
							<div className="w-5 h-5 border-2 border-border border-t-primary rounded-full animate-spin" />
						</div>
					) : (
						<FormCategoriesList
							categories={observationCategories}
							onCreateNewCategoryClick={() => setIsOpen(true)}
							itemHref={(category) =>
								`/observations/forms/?status=published&category=${category.name}&categoryId=${category.id}`
							}
							admin={true}
						/>
					)}
				</div>
			)}

			{category && <FormStatusTabPanel category={category} />}

			<CreateNewCategoryModal
				isOpen={isOpen}
				onClose={() => setIsOpen(false)}
			/>
		</div>
	);
};

export default EditForms;
